// Registro por QR, en un solo lugar y a la vista (8-oct-2026, Carlos: «el QR está muy oculto»).
// Antes vivía al final de Mi Negocio → Kiosco y promoción, mezclado con los QR de redes. Datos del
// 8-oct: solo 38 de 377 negocios de lealtad lo tienen prendido, pero de esos arranca el 61 % (≥3 clientes)
// contra 18 % del resto; y el 16 % de las solicitudes vence sin que nadie las confirme (24 h).
// Se usa en: Mi Negocio › Cómo registro visitas (único lugar donde se enciende desde 24-oct-2026) y Visitas por QR. Se guarda solo (PUT /loyalty/settings).
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Printer, Smartphone, CheckCircle2, Loader2 } from 'lucide-react';
import { baseApi, BaseApiError } from '../../api/baseClient';
import { useAuth } from '../../context/AuthContext';
import { useError } from '../../context/ErrorContext';

const QR_ID = 'qrcode-checkin';

// Cartel tamaño carta para imprimir y pegar en el mostrador: el QR + 3 pasos. Se arma en una ventana
// aparte y se manda a imprimir (el navegador ofrece «Guardar como PDF»).
function imprimirCartel(negocio: string, url: string) {
  const svg = document.getElementById(QR_ID)?.outerHTML || '';
  const w = window.open('', '_blank', 'width=820,height=1000');
  if (!w) return;
  const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Cartel QR · ${esc(negocio)}</title>
<style>
  @page { size: letter; margin: 0 }
  body { margin: 0; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; color: #0E1E3A; }
  .hoja { width: 8.5in; height: 11in; box-sizing: border-box; padding: .7in .8in; display: flex; flex-direction: column; align-items: center; text-align: center; }
  .neg { font-size: 22pt; font-weight: 800; margin: 0; }
  h1 { font-size: 40pt; line-height: 1.05; margin: .25in 0 .1in; letter-spacing: -.02em; }
  .sub { font-size: 16pt; color: #475569; margin: 0 0 .35in; }
  .qr { background: #fff; border: 6px solid #0057B8; border-radius: 28px; padding: 18px; }
  .qr svg { width: 3.6in; height: 3.6in; display: block; }
  ol { list-style: none; padding: 0; margin: .4in 0 0; display: flex; gap: .3in; }
  li { width: 2.1in; font-size: 13pt; }
  li b { display: flex; align-items: center; justify-content: center; margin: 0 auto 8px; width: 40px; height: 40px; border-radius: 50%; background: #0057B8; color: #fff; font-size: 16pt; }
  .pie { margin-top: auto; font-size: 11pt; color: #64748b; }
</style></head><body><div class="hoja">
  <p class="neg">${esc(negocio)}</p>
  <h1>¡Gana premios<br>en cada visita!</h1>
  <p class="sub">Escanea con la cámara de tu celular. Sin descargar nada.</p>
  <div class="qr">${svg}</div>
  <ol><li><b>1</b>Escanea el código</li><li><b>2</b>Escribe tu número de celular</li><li><b>3</b>Enséñale tu pantalla a quien te atiende</li></ol>
  <p class="pie">¿No te abre? Entra a ${esc(url)}<br>Programa de lealtad con Lealify</p>
</div><script>window.onload = () => setTimeout(() => window.print(), 300);<\/script></body></html>`);
  w.document.close();
}

interface Props {
  /** 'compacta' = opción dentro de Programa de lealtad; 'completa' = cabecera de la pantalla Visitas por QR. */
  variante?: 'compacta' | 'completa';
}

const QrCheckinCard: React.FC<Props> = ({ variante = 'completa' }) => {
  const { currentUser } = useAuth();
  const { addError } = useError();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    baseApi.get<{ settings?: { qr_checkin_enabled?: number } }>('/loyalty/settings')
      .then((r) => { if (alive) setEnabled(!!r?.settings?.qr_checkin_enabled); })
      .catch(() => { if (alive) setEnabled(false); });
    return () => { alive = false; };
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    const prev = enabled;
    setEnabled(next);
    try {
      await baseApi.put('/loyalty/settings', { qr_checkin_enabled: next ? 1 : 0 });
      window.dispatchEvent(new Event('lealify:qr-checkin-changed')); // el menú y «Para hoy» se actualizan
    } catch (error) {
      setEnabled(prev);
      const detail = error instanceof BaseApiError ? error.message : '';
      addError(detail ? `No se pudo guardar: ${detail}` : 'No se pudo cambiar el registro por QR.', 'unknown', 'qr-checkin-toggle');
    } finally {
      setSaving(false);
    }
  };

  // Misma URL que el panel anterior: siempre por app.lealify.com (los <slug>.lealify.com no tienen vhost).
  const slug = currentUser?.businessId || '';
  const branchId = currentUser?.branches?.find((b) => b.isActive)?.id || currentUser?.branches?.[0]?.id || '';
  const url = `https://app.lealify.com/l/${slug}${branchId ? `/${branchId}` : ''}`;
  const negocio = currentUser?.businessName || 'Mi negocio';

  const interruptor = (
    <button
      type="button"
      role="switch"
      aria-checked={!!enabled}
      disabled={enabled === null || saving}
      onClick={() => toggle(!enabled)}
      className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? 'bg-[#0057B8]' : 'bg-slate-300 dark:bg-slate-600'}`}
    >
      <span className="sr-only">{enabled ? 'Desactivar registro por QR' : 'Activar registro por QR'}</span>
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  const qr = (size: number) => (
    <div className="rounded-2xl bg-white p-2 shadow-sm ring-1 ring-black/5 flex-shrink-0">
      <QRCodeSVG id={QR_ID} value={url} size={size} bgColor="#ffffff" fgColor="#0E1E3A" level="H" includeMargin />
    </div>
  );

  const botonCartel = (
    <button
      type="button"
      onClick={() => imprimirCartel(negocio, url)}
      className="inline-flex items-center gap-2 rounded-full bg-[#0057B8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004494] transition-colors"
    >
      <Printer className="h-4 w-4" /> Imprimir cartel
    </button>
  );

  if (variante === 'compacta') {
    return (
      <div className={`rounded-xl border-2 p-4 transition-colors ${enabled ? 'border-[#0057B8] bg-[#0057B8]/5' : 'border-line bg-surface'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0057B8]/10 text-[#0057B8] dark:text-blue-300"><QrCode className="h-5 w-5" /></span>
            <div>
              <p className="font-semibold text-content flex items-center gap-2">
                QR en el mostrador
                <span className="text-[10px] font-bold uppercase tracking-wide bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300 px-1.5 py-0.5 rounded">Gratis</span>
              </p>
              <p className="text-xs text-content-3 mt-0.5">Tu cliente lo escanea con su celular y se registra solo. Sin tablet.</p>
            </div>
          </div>
          {saving ? <Loader2 className="h-5 w-5 animate-spin text-content-3" /> : interruptor}
        </div>
        {enabled && (
          <div className="mt-4 flex items-center gap-4">
            {qr(96)}
            <div className="space-y-2">
              {botonCartel}
              <RouterLink to="/dashboard/checkin-approvals" className="block text-sm font-medium text-accent hover:underline">Ver visitas por QR →</RouterLink>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Completa: cabecera de «Visitas por QR». Apagado = invitación grande con los 3 pasos.
  return (
    <div className="rounded-2xl bg-[#0E1E3A] text-white overflow-hidden">
      <div className="flex flex-col lg:flex-row gap-6 p-6 lg:p-8 lg:items-center">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl lg:text-2xl font-display font-extrabold tracking-tight">
              {enabled ? 'Tu QR está activo' : 'Que tus clientes se registren solos con un QR'}
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-wide bg-green-400/20 text-green-300 px-2 py-0.5 rounded">Gratis</span>
          </div>
          <p className="mt-2 text-sm text-white/75 max-w-2xl">
            {enabled
              ? 'Imprime el cartel y pégalo en tu mostrador. Cada visita que pidan llega aquí abajo: confírmala en menos de 24 h o se pierde.'
              : 'Sin tablet y sin capturar datos: lo imprimes, lo pegas en el mostrador y tu cliente lo escanea con su celular. Tú solo confirmas su visita con un toque.'}
          </p>
          <ol className="mt-5 grid gap-3 sm:grid-cols-3 text-sm">
            {[[Printer, 'Imprime y pega el cartel'], [Smartphone, 'Tu cliente lo escanea y pone su número'], [CheckCircle2, 'Confirmas la visita y recibe sus puntos']].map(([Icon, t], i) => {
              const I = Icon as React.ElementType;
              return (
                <li key={i} className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
                  <I className="h-5 w-5 text-blue-300 flex-shrink-0" /> <span>{t as string}</span>
                </li>
              );
            })}
          </ol>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {enabled ? botonCartel : (
              <button type="button" disabled={enabled === null || saving} onClick={() => toggle(true)}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#0E1E3A] hover:bg-blue-50 transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Activar mi QR
              </button>
            )}
            {enabled && (
              <span className="flex items-center gap-2 text-sm text-white/75">{interruptor} Activado</span>
            )}
          </div>
        </div>
        {enabled && <div className="self-center">{qr(176)}</div>}
      </div>
    </div>
  );
};

export default QrCheckinCard;
